import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';
import { Building2 } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaySuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    payment_number: `PAY-${Date.now()}`,
    payment_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    supplier_name: '',
    amount: 0,
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    reference_number: '',
    notes: '',
    paid_by: 'Admin'
  });

  useEffect(() => {
    fetchSuppliers();
    fetchPayments();
    fetchBanks();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/inventory/suppliers');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setSuppliers(data.filter(s => s.balance > 0));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/inventory/supplier-payments');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setPayments(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/banks');
      const data = Array.isArray(response.data) ? response.data : [];
      setBanks(data);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({ ...formData, supplier_id: supplierId, supplier_name: supplier.name, amount: supplier.balance });
    }
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData({ ...formData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const isBankPayment = formData.payment_mode !== 'Cash';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate bank selection for non-cash payments
    if (isBankPayment && !formData.bank_id) {
      alert('Please select a bank for non-cash payment');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post('/api/inventory/supplier-payments', formData);
      if (response.status === 200 || response.data) {
        alert('Payment recorded successfully! Entry added to ' + (formData.payment_mode === 'Cash' ? 'Cash Register' : 'Bank Register'));
        fetchPayments();
        fetchSuppliers();
        fetchBanks(); // Refresh bank balances
        setFormData({
          payment_number: `PAY-${Date.now()}`,
          payment_date: new Date().toISOString().split('T')[0],
          supplier_id: '',
          supplier_name: '',
          amount: 0,
          payment_mode: 'Cash',
          bank_id: '',
          bank_name: '',
          reference_number: '',
          notes: '',
          paid_by: 'Admin'
        });
      } else {
        alert('Failed to record payment');
      }
    } catch (error) {
      alert('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pay Suppliers</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Payment Number</Label>
                <Input value={formData.payment_number} disabled />
              </div>
              <div>
                <Label>Payment Date *</Label>
                <Input type="date" required value={formData.payment_date} onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })} />
              </div>
              <div>
                <Label>Paid By</Label>
                <Input value={formData.paid_by} onChange={(e) => setFormData({ ...formData, paid_by: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Supplier *</Label>
              <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier with due balance" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} - Due: ₹{supplier.balance}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Mode *</Label>
                <Select value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value, bank_id: '', bank_name: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} placeholder="Transaction/Cheque #" />
              </div>
            </div>
            
            {/* Bank Selection for non-cash payments */}
            {isBankPayment && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <Label className="flex items-center gap-2 text-blue-800 mb-2">
                  <Building2 className="w-4 h-4" />
                  Select Bank *
                </Label>
                <Select value={formData.bank_id} onValueChange={handleBankChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="-- Select Bank --" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((bank) => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.bank_name} (Balance: ₹{bank.current_balance?.toLocaleString('en-IN') || 0})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.bank_id && (
                  <p className="text-xs text-blue-600 mt-1">Amount will be deducted from {formData.bank_name}</p>
                )}
              </div>
            )}
            
            <div>
              <Label>Amount *</Label>
              <Input type="number" step="0.01" required value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes" />
            </div>
            <Button type="submit" disabled={loading} className="w-full">{loading ? 'Processing...' : 'Record Payment'}</Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Payment #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Supplier</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Mode</th>
                  <th className="text-left p-3">Paid By</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan="6" className="text-center p-4 text-gray-500">No payments found.</td></tr>
                ) : (
                  payments.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{payment.payment_number}</td>
                      <td className="p-3">{formatDate(payment.payment_date)}</td>
                      <td className="p-3">{payment.supplier_name}</td>
                      <td className="p-3 text-green-600 font-medium">₹{payment.amount}</td>
                      <td className="p-3">{payment.payment_mode}</td>
                      <td className="p-3">{payment.paid_by}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaySuppliers;